/**
 * useUserApps — fetches `GET /v1/me/apps` from the Pulse agent-hub Worker.
 *
 * Worker contract (Chunk 4 / E2E-2 verifies live):
 *
 *   200 OK
 *   {
 *     "apps":      ["admin", "hub", "projects"],
 *     "app_roles": { "admin": "admin", "hub": "member", "projects": "viewer" },
 *     "source":    "supabase" | "kv-cache" | "fallback"
 *   }
 *
 * Auth: Bearer access_token (Supabase session JWT).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { PulseRoleSlug } from "@pulse-business-ai/types";

import { readLauncherEnv } from "../env.js";
import type { LauncherApp } from "../components/AppTile.js";

export interface UserAppsPayload {
  readonly apps: ReadonlyArray<LauncherApp>;
  readonly appRoles: Readonly<Partial<Record<LauncherApp, PulseRoleSlug>>>;
  readonly source: string;
}

export interface UseUserAppsResult extends UserAppsPayload {
  readonly loading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

const KNOWN_APPS: ReadonlySet<LauncherApp> = new Set([
  "admin",
  "hub",
  "projects",
  "pulse-app",
  "launcher",
]);
const KNOWN_ROLES: ReadonlySet<PulseRoleSlug> = new Set([
  "admin",
  "member",
  "viewer",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalise(json: unknown): UserAppsPayload {
  if (!isRecord(json)) return { apps: [], appRoles: {}, source: "unknown" };

  const rawApps = Array.isArray(json.apps) ? json.apps : [];
  const apps: LauncherApp[] = [];
  for (const entry of rawApps) {
    if (typeof entry === "string" && KNOWN_APPS.has(entry as LauncherApp)) {
      apps.push(entry as LauncherApp);
    }
  }

  const rawRoles = isRecord(json.app_roles) ? json.app_roles : {};
  const appRoles: Partial<Record<LauncherApp, PulseRoleSlug>> = {};
  for (const [k, v] of Object.entries(rawRoles)) {
    if (
      KNOWN_APPS.has(k as LauncherApp) &&
      typeof v === "string" &&
      KNOWN_ROLES.has(v as PulseRoleSlug)
    ) {
      appRoles[k as LauncherApp] = v as PulseRoleSlug;
    }
  }

  const source = typeof json.source === "string" ? json.source : "unknown";

  return { apps, appRoles, source };
}

/**
 * Hook — fetches `/v1/me/apps`. Re-runs whenever the bearer token changes.
 * Pass `null` while auth is still resolving; the hook stays in `loading`
 * state and emits no fetch.
 */
export function useUserApps(accessToken: string | null): UseUserAppsResult {
  const [data, setData] = useState<UserAppsPayload>({
    apps: [],
    appRoles: {},
    source: "pending",
  });
  const [loading, setLoading] = useState(accessToken !== null);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  // Pin env to first render so test mocks remain stable.
  const envRef = useRef(readLauncherEnv());

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const url = `${envRef.current.agentHubUrl.replace(/\/+$/, "")}/v1/me/apps`;

    void (async () => {
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`useUserApps: ${res.status} ${res.statusText}`);
        }
        const json: unknown = await res.json();
        if (controller.signal.aborted) return;
        setData(normalise(json));
        setLoading(false);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [accessToken, tick]);

  return { ...data, loading, error, refetch };
}
